import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ChevronRight, Edit3, Send, CheckCircle, XCircle,
  Loader2, AlertCircle, X, FileText, Clock, User, DollarSign,
  Building2, ShieldCheck, CreditCard, Hash, ExternalLink, Briefcase
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── constants ───────────── */
const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_branch_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  returned: 'bg-red-50 text-red-700 border-red-200',
  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_gm_review: 'bg-orange-50 text-orange-700 border-orange-200',
  pending_accounts_review: 'bg-purple-50 text-purple-700 border-purple-200',
  processed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_branch_approval: 'Pending Branch Approval',
  returned: 'Returned',
  submitted: 'Pending Business Review',
  pending_gm_review: 'Pending GM Review',
  pending_accounts_review: 'Pending Accounts Review',
  processed: 'Processed',
};

const WORKFLOW_STEPS = [
  { key: 'draft', label: 'Draft Created' },
  { key: 'pending_branch_approval', label: 'Submitted' },
  { key: 'submitted', label: 'Branch Approved' },
  { key: 'pending_gm_review', label: 'Business Reviewed' },
  { key: 'pending_accounts_review', label: 'GM Approved' },
  { key: 'processed', label: 'Processed' },
];

/* ───────────── helpers ───────────── */
const getRoleInfo = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return { role: user?.roles?.[0]?.name || '', user };
  } catch { return { role: '', user: null }; }
};

/** Normalize role: "Branch Worker" | "Branch-Worker" | "branch_worker" → "branchworker" */
const norm = (role) => role.toLowerCase().replace(/[\s_-]/g, '');

const can = {
  edit: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'branchworker') && ['draft', 'returned'].includes(status);
  },
  submit: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'branchworker') && ['draft', 'returned'].includes(status);
  },
  approve: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'branch') && status === 'pending_branch_approval';
  },
  return_: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'branch') && status === 'pending_branch_approval';
  },
  businessReview: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'business') && status === 'submitted';
  },
  gmReview: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'gm') && status === 'pending_gm_review';
  },
  accountsReview: (role, status) => {
    const n = norm(role);
    return (n === 'admin' || n === 'accounts') && status === 'pending_accounts_review';
  },
};

const fmtCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  return `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

/* ───────────── component ───────────── */
export default function StoreIncidentalDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [roleInfo] = useState(getRoleInfo());
  const role = roleInfo.role;

  /* data */
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* action states */
  const [actionLoading, setActionLoading] = useState('');
  const [returnRemark, setReturnRemark] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);

  /* business review state */
  const [businessItems, setBusinessItems] = useState([]);
  const [showBusinessPanel, setShowBusinessPanel] = useState(false);
  const [businessAction, setBusinessAction] = useState('gm');
  const [businessReturnRemark, setBusinessReturnRemark] = useState('');

  /* gm review state */
  const [gmAction, setGmAction] = useState('accounts');
  const [gmRemark, setGmRemark] = useState('');

  /* accounts review state */
  const [accountsItems, setAccountsItems] = useState([]);
  const [showAccountsPanel, setShowAccountsPanel] = useState(false);

  /* ── fetch record ── */
  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`store-incidentals/${id}`);
      const d = res.data?.data || res.data;
      setRecord(d);

      // Init business items
      if (d.items) {
        setBusinessItems(d.items.map(item => ({
          id: item.id,
          incidental_amount_paid: item.incidental_amount_paid || '',
          penalty: item.penalty || '',
          incidental_amount_pending: item.incidental_amount_pending || '',
          remark: item.remark_business || '',
        })));
        setAccountsItems(d.items.map(item => ({
          id: item.id,
          utr_no: item.utr_no || '',
          remark: item.remark_accounts || '',
        })));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load record');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  /* ── actions ── */
  const handleSubmitForApproval = async () => {
    setActionLoading('submit');
    setError('');
    try {
      await axios.post(`store-incidentals/${id}/submit`);
      setSuccess('Submitted for branch approval!');
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setActionLoading('');
    }
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    setError('');
    try {
      await axios.patch(`store-incidentals/${id}/branch-approve`);
      setSuccess('Approved successfully!');
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading('');
    }
  };

  const handleReturn = async () => {
    if (!returnRemark.trim()) {
      setError('Please provide a remark for returning.');
      return;
    }
    setActionLoading('return');
    setError('');
    try {
      await axios.patch(`store-incidentals/${id}/branch-return`, { remark: returnRemark.trim() });
      setSuccess('Returned to branch worker.');
      setShowReturnModal(false);
      setReturnRemark('');
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to return');
    } finally {
      setActionLoading('');
    }
  };

  const handleBusinessReview = async () => {
    // Validate: remark required when returning to branch
    if (businessAction === 'branch' && !businessReturnRemark.trim()) {
      setError('A remark is required when returning to branch.');
      return;
    }

    // Validate item amounts (only relevant when forwarding to GM)
    if (businessAction === 'gm') {
      for (const item of businessItems) {
        const penalty = parseFloat(item.penalty) || 0;
        const pending = parseFloat(item.incidental_amount_pending) || 0;
        const paid = parseFloat(item.incidental_amount_paid) || 0;

        if (penalty < 0 || pending < 0) {
          setError('Penalty and pending amounts cannot be negative.');
          return;
        }
        if (paid < 0) {
          setError('Penalty + Pending cannot exceed the Claimed amount.');
          return;
        }
      }
    }

    setActionLoading('business');
    setError('');
    try {
      const body = { action: businessAction };
      if (businessAction === 'gm') {
        body.items = businessItems.map(item => {
          const obj = { id: item.id };
          if (item.incidental_amount_paid !== '') obj.incidental_amount_paid = parseFloat(item.incidental_amount_paid);
          if (item.penalty !== '') obj.penalty = parseFloat(item.penalty);
          if (item.incidental_amount_pending !== '') obj.incidental_amount_pending = parseFloat(item.incidental_amount_pending);
          if (item.remark) obj.remark = item.remark;
          return obj;
        });
      }
      if (businessReturnRemark.trim()) body.remark = businessReturnRemark.trim();

      await axios.patch(`store-incidentals/${id}/business-review`, body);
      const msgs = {
        gm: 'Business review completed — forwarded to GM.',
        branch: 'Record returned to branch for correction.',
      };
      setSuccess(msgs[businessAction] || 'Business review submitted.');
      setShowBusinessPanel(false);
      setBusinessReturnRemark('');
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit business review');
    } finally {
      setActionLoading('');
    }
  };

  const handleGmReview = async () => {
    if ((gmAction === 'business' || gmAction === 'branch') && !gmRemark.trim()) {
      setError(`A remark is required when sending back to ${gmAction}.`);
      return;
    }
    setActionLoading('gm');
    setError('');
    try {
      const body = { action: gmAction };
      if (gmRemark.trim()) body.remark = gmRemark.trim();
      await axios.patch(`store-incidentals/${id}/gm-review`, body);
      const msgs = {
        accounts: 'Forwarded to Accounts for review.',
        business: 'Sent back to Business for revision.',
        branch: 'Sent back to Branch for correction.',
      };
      setSuccess(msgs[gmAction] || 'GM review submitted.');
      setGmRemark('');
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit GM review');
    } finally {
      setActionLoading('');
    }
  };

  const handleAccountsReview = async () => {
    // Validate UTR required
    for (const item of accountsItems) {
      if (!item.utr_no.trim()) {
        setError('UTR No. is required for every item row.');
        return;
      }
    }

    setActionLoading('accounts');
    setError('');
    try {
      await axios.patch(`store-incidentals/${id}/accounts-review`, {
        items: accountsItems.map(item => {
          const obj = { id: item.id, utr_no: item.utr_no.trim() };
          if (item.remark) obj.remark = item.remark;
          return obj;
        })
      });
      setSuccess('Accounts review completed! Record processed.');
      setShowAccountsPanel(false);
      await fetchRecord();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit accounts review');
    } finally {
      setActionLoading('');
    }
  };

  /* helpers for business item editing */
  const updateBusinessItem = (itemId, field, value) => {
    setBusinessItems(prev => prev.map(i => {
      if (i.id !== itemId) return i;
      const updated = { ...i, [field]: value };

      // Auto-derive paid = claimed - penalty - pending
      const claimed = parseFloat(record?.items?.find(r => r.id === itemId)?.incidental_amount_claimed || 0);
      const penalty = parseFloat(field === 'penalty' ? value : updated.penalty) || 0;
      const pending = parseFloat(field === 'incidental_amount_pending' ? value : updated.incidental_amount_pending) || 0;

      // Always auto-calculate paid; user only controls penalty + pending
      const derived = claimed - penalty - pending;
      updated.incidental_amount_paid = derived >= 0 ? derived.toFixed(2) : '0.00';

      return updated;
    }));
  };

  const updateAccountsItem = (itemId, field, value) => {
    setAccountsItems(prev => prev.map(i => i.id === itemId ? { ...i, [field]: value } : i));
  };

  /* workflow step index */
  const getStepIndex = (status) => {
    if (status === 'returned') return 1;
    const idx = WORKFLOW_STEPS.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
  };

  /* ── loading state ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-blue-600">
          <Loader2 size={40} className="animate-spin" />
          <span className="font-bold text-sm">Loading details...</span>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-slate-700">Record not found</h3>
          <button onClick={() => navigate('/incidental/store')} className="mt-4 text-blue-600 font-bold text-sm hover:underline">Go back to list</button>
        </div>
      </div>
    );
  }

  const status = record.status;
  const items = record.items || [];
  const stepIdx = getStepIndex(status);

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="w-full max-w-[1400px] mx-auto">

        {/* ── Breadcrumb ── */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/incidental/store')}
            className="flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">
            <ArrowLeft size={18} /> Back to List
          </button>
          <ChevronRight size={14} className="text-slate-300" />
          <span className="text-sm font-bold text-slate-800">Record Details</span>
        </motion.div>

        {/* ── Messages ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
              <AlertCircle size={20} className="shrink-0 mt-0.5" /> <span className="flex-1">{error}</span>
              <button onClick={() => setError('')} className="p-1 hover:bg-red-100 rounded-lg"><X size={16} /></button>
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm font-semibold">
              <CheckCircle size={20} /> {success}
              <button onClick={() => setSuccess('')} className="ml-auto p-1 hover:bg-emerald-100 rounded-lg"><X size={16} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Header Card ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl md:text-3xl font-black text-blue-800 tracking-tight">Store Incidental</h1>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold border ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                  {STATUS_LABELS[status] || status}
                </span>
              </div>
              <p className="text-xs font-mono text-slate-400">ID: {record.reference_id || '—'}</p>
            </div>
            <div className="flex items-center gap-2">
              {can.edit(role, status) && (
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  onClick={() => navigate(`/incidental/store/${id}/edit`)}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-amber-100 transition-all">
                  <Edit3 size={16} /> Edit
                </motion.button>
              )}
              {can.submit(role, status) && (
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  onClick={handleSubmitForApproval} disabled={actionLoading === 'submit' || items.length === 0}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-blue-100 transition-all">
                  {actionLoading === 'submit' ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Submit for Approval
                </motion.button>
              )}
            </div>
          </div>

          {/* Header Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Season', value: record.season?.name, icon: Clock },
              { label: 'Commodity', value: record.commodity?.commodityname || record.commodity?.name, icon: FileText },
              { label: 'State', value: record.state?.name, icon: Building2 },
              { label: 'Branch', value: record.branch?.first_name || record.branch?.name || record.branch?.branch_name, icon: Building2 },
              { label: 'Procurement Qty', value: record.procurement_quantity ? parseFloat(record.procurement_quantity).toLocaleString('en-IN') : '—', icon: Hash },
            ].map(info => (
              <div key={info.label} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <info.icon size={14} className="text-slate-400" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{info.label}</p>
                </div>
                <p className="text-sm font-bold text-slate-800">{info.value || '—'}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[
              { label: 'Total Claimed', value: fmtCurrency(record.total_claimed_amount), color: 'text-blue-700' },
              { label: 'Total Paid', value: fmtCurrency(record.total_paid_amount), color: 'text-emerald-700' },
              { label: 'Total Penalty', value: fmtCurrency(record.total_penalty_amount), color: 'text-red-600' },
              { label: 'Total Pending', value: fmtCurrency(record.total_pending_amount), color: 'text-amber-700' },
            ].map(t => (
              <div key={t.label} className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{t.label}</p>
                <p className={`text-lg font-black ${t.color}`}>{t.value}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ── Workflow Timeline ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
          <h2 className="text-lg font-black text-slate-800 mb-5 flex items-center gap-2">
            <Clock size={20} className="text-blue-600" /> Workflow Timeline
          </h2>

          {/* Steps */}
          <div className="flex items-center gap-0 overflow-x-auto pb-2 no-scrollbar">
            {WORKFLOW_STEPS.map((step, idx) => {
              const isCompleted = idx < stepIdx || (idx === stepIdx && status === 'processed');
              const isCurrent = idx === stepIdx && status !== 'processed';
              const isReturned = status === 'returned' && idx === 1;

              return (
                <React.Fragment key={step.key}>
                  <div className="flex flex-col items-center min-w-[120px]">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all
                      ${isReturned ? 'bg-red-500 text-white ring-4 ring-red-100' :
                        isCompleted ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' :
                          isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                            'bg-slate-200 text-slate-500'}`}>
                      {isReturned ? <XCircle size={18} /> : isCompleted ? <CheckCircle size={18} /> : idx + 1}
                    </div>
                    <span className={`text-[10px] font-bold mt-2 text-center leading-tight
                      ${isReturned ? 'text-red-600' : isCompleted ? 'text-emerald-600' : isCurrent ? 'text-blue-600' : 'text-slate-400'}`}>
                      {isReturned ? 'Returned' : step.label}
                    </span>
                  </div>
                  {idx < WORKFLOW_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 min-w-[30px] ${idx < stepIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Workflow Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
            {[
              // { label: 'Created By', value: record.createdBy?.first_name || record.createdBy?.name || (typeof record.createdBy === 'string' ? record.createdBy : null), time: record.createdAt },
              { label: 'Submitted By', value: record.submittedBy?.first_name || record.submittedBy?.name || (typeof record.submittedBy === 'string' ? record.submittedBy : null), time: record.submittedAt },
              { label: 'Branch Approved By', value: record.branchApprovedBy?.first_name || record.branchApprovedBy?.name || (typeof record.branchApprovedBy === 'string' ? record.branchApprovedBy : null), time: record.branchApprovedAt },
              { label: 'Returned By', value: record.returnedBy?.first_name || record.returnedBy?.name || (typeof record.returnedBy === 'string' ? record.returnedBy : null), time: record.returnedAt },
              { label: 'GM Reviewed By', value: record.gmReviewedBy?.first_name || record.gmReviewedBy?.name || (typeof record.gmReviewedBy === 'string' ? record.gmReviewedBy : null), time: record.gmReviewedAt },
              { label: 'Processed By', value: record.processedBy?.first_name || record.processedBy?.name || (typeof record.processedBy === 'string' ? record.processedBy : null), time: record.processedAt },
            ].filter(m => m.value).map(m => (
              <div key={m.label} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100">
                <User size={14} className="text-slate-400 shrink-0" />
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{m.label}</p>
                  <p className="text-xs font-bold text-slate-700">{m.value}</p>
                  {m.time && <p className="text-[10px] text-slate-400">{fmtDate(m.time)}</p>}
                </div>
              </div>
            ))}
          </div>

          {/* Return remark */}
          {record.returnRemark && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-2xl">
              <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-1">Return Remark</p>
              <p className="text-sm text-red-700 font-medium">{record.returnRemark}</p>
            </div>
          )}
        </motion.div>

        {/* ── Items Table ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden mb-6">
          <div className="p-6 md:p-8 border-b border-slate-100">
            <h2 className="text-xl font-black text-[#1E3A8A] flex items-center gap-2">
              <FileText size={20} /> Item Details
            </h2>
            <p className="text-sm text-slate-500 mt-1">{items.length} item{items.length !== 1 ? 's' : ''} in this record</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200">
                  {['#', 'Particular', 'Incidental Rate', 'Approval Amount', 'Claimed Amount', 'Penalty', 'Paid Amount', 'Pending Amount', 'UTR No.', 'Remark', 'Document'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-12 text-center text-slate-400 text-sm font-medium">No items found</td>
                  </tr>
                ) : (
                  items.map((item, idx) => (
                    <tr key={item.id} className="hover:bg-slate-50/30 transition-colors">
                      <td className="px-4 py-4 text-xs font-mono text-slate-400">{idx + 1}</td>
                      <td className="px-4 py-4 text-sm font-bold text-slate-800 min-w-[180px]">{item.particular?.name || '—'}</td>
                      <td className="px-4 py-4 text-sm font-mono text-slate-600">{item.incidental_rate ? parseFloat(item.incidental_rate).toFixed(2) : '—'}</td>
                      <td className="px-4 py-4 text-sm font-bold text-slate-700">{fmtCurrency(item.approval_amount)}</td>
                      <td className="px-4 py-4 text-sm font-bold text-blue-700">{fmtCurrency(item.incidental_amount_claimed)}</td>
                      <td className="px-4 py-4 text-sm font-bold text-red-600">{fmtCurrency(item.penalty)}</td>
                      <td className="px-4 py-4 text-sm font-bold text-emerald-700">{fmtCurrency(item.incidental_amount_paid)}</td>
                      <td className="px-4 py-4 text-sm font-bold text-amber-700">{fmtCurrency(item.incidental_amount_pending)}</td>
                      <td className="px-4 py-4 text-xs font-mono text-slate-600">{item.utr_no || '—'}</td>
                      <td className="px-4 py-4 text-xs text-slate-500 max-w-[150px] truncate" title={item.remark}>{item.remark || '—'}</td>
                      <td className="px-4 py-4">
                        {(() => {
                          const docs = Array.isArray(item.supporting_document)
                            ? item.supporting_document
                            : item.supporting_document
                            ? [item.supporting_document]
                            : [];
                          if (docs.length === 0) return <span className="text-xs text-slate-400">—</span>;
                          return (
                            <div className="flex flex-col gap-1">
                              {docs.map((url, di) => (
                                <a
                                  key={di}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-bold"
                                >
                                  <ExternalLink size={13} /> Doc {di + 1}
                                </a>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* ── Branch Approval Action Panel ── */}
        {can.approve(role, status) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
            <h2 className="text-lg font-black text-slate-800 mb-4 flex items-center gap-2">
              <ShieldCheck size={20} className="text-blue-600" /> Branch Approval
            </h2>
            <p className="text-sm text-slate-500 mb-6">Review the incidental record above and approve or return it.</p>
            <div className="flex items-center gap-3">
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={handleApprove} disabled={!!actionLoading}
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-emerald-100 transition-all">
                {actionLoading === 'approve' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                Approve
              </motion.button>
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setShowReturnModal(true)} disabled={!!actionLoading}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-red-100 transition-all">
                <XCircle size={16} /> Return
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── Business Review Action Panel ── */}
        {can.businessReview(role, status) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
            <div className="flex items-center mb-2">
              <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <DollarSign size={20} className="text-blue-600" /> Business Review
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-5">Fill in payment details below, then forward to GM for approval or return to branch for corrections.</p>

            {/* Action selector */}
            <div className="flex flex-wrap gap-3 mb-5">
              {[
                { value: 'gm', label: 'Forward to GM', color: 'blue' },
                { value: 'branch', label: 'Return to Branch', color: 'red' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setBusinessAction(opt.value); setBusinessReturnRemark(''); }}
                  className={`px-5 py-2.5 rounded-xl font-bold text-sm border transition-all ${
                    businessAction === opt.value
                      ? opt.value === 'gm'
                        ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100'
                        : 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-100'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Remark — required for branch, optional for gm */}
            <div className="mb-5">
              <label className="text-sm font-bold text-slate-700 mb-2 block">
                Remark
                {businessAction === 'branch' && <span className="text-red-500 ml-1">*</span>}
                {businessAction === 'gm' && <span className="text-slate-400 text-xs font-normal ml-2">(optional)</span>}
              </label>
              <textarea
                rows={3}
                value={businessReturnRemark}
                onChange={e => setBusinessReturnRemark(e.target.value)}
                placeholder={
                  businessAction === 'gm'
                    ? 'Optional remark for GM...'
                    : 'Explain what corrections are needed at branch...'
                }
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-400 outline-none transition-all bg-slate-50 resize-none"
              />
            </div>

            <AnimatePresence>
              {businessAction === 'gm' && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  {/* Helper hint */}
                  <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 mb-4">
                    <AlertCircle size={15} className="text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-700 leading-relaxed">
                      Enter <strong>Penalty</strong> first — <strong>Paid Amount</strong> is calculated automatically as <em>Claimed − Penalty − Pending</em>.
                      Optionally enter a <strong>Pending</strong> amount; if left blank, it defaults to <em>₹0</em>.
                    </p>
                  </div>
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-left border-collapse min-w-[900px]">
                      <thead>
                        <tr className="bg-slate-50">
                          {['Particular', 'Claimed', 'Penalty', 'Paid Amount', 'Pending', 'Remark'].map(h => (
                            <th key={h} className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item) => {
                          const bi = businessItems.find(b => b.id === item.id) || {};
                          const claimed = parseFloat(item.incidental_amount_claimed || 0);
                          const penalty = parseFloat(bi.penalty) || 0;
                          const pending = parseFloat(bi.incidental_amount_pending) || 0;
                          const derivedPaid = Math.max(0, claimed - penalty - pending);
                          const isOverDeducted = penalty + pending > claimed;

                          return (
                            <tr key={item.id} className="hover:bg-slate-50/30">
                              {/* Particular */}
                              <td className="px-4 py-3 text-sm font-bold text-slate-800 min-w-[160px]">{item.particular?.name}</td>

                              {/* Claimed — read-only reference */}
                              <td className="px-4 py-3 text-sm font-bold text-blue-700 whitespace-nowrap">{fmtCurrency(item.incidental_amount_claimed)}</td>

                              {/* Penalty — editable, goes first */}
                              <td className="px-4 py-3">
                                <input
                                  type="number" min="0" placeholder="0.00"
                                  value={bi.penalty || ''}
                                  onChange={e => updateBusinessItem(item.id, 'penalty', e.target.value)}
                                  className={`w-full min-w-[110px] border rounded-lg px-3 py-2 text-sm focus:ring-2 outline-none bg-white transition-colors ${
                                    isOverDeducted
                                      ? 'border-red-300 focus:ring-red-400 bg-red-50'
                                      : 'border-slate-200 focus:ring-red-400'
                                  }`}
                                />
                              </td>

                              {/* Paid Amount — auto-derived, read-only display */}
                              <td className="px-4 py-3">
                                <div className={`w-full min-w-[120px] rounded-lg px-3 py-2 text-sm font-bold border ${
                                  isOverDeducted
                                    ? 'bg-red-50 border-red-200 text-red-600'
                                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                }`}>
                                  {isOverDeducted
                                    ? <span className="flex items-center gap-1"><AlertCircle size={12} /> Exceeds claimed</span>
                                    : `₹${derivedPaid.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  }
                                </div>
                              </td>

                              {/* Pending — optional, editable */}
                              <td className="px-4 py-3">
                                <input
                                  type="number" min="0" placeholder="0.00"
                                  value={bi.incidental_amount_pending || ''}
                                  onChange={e => updateBusinessItem(item.id, 'incidental_amount_pending', e.target.value)}
                                  className="w-full min-w-[110px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 outline-none bg-white transition-colors"
                                />
                              </td>

                              {/* Remark */}
                              <td className="px-4 py-3">
                                <input type="text" placeholder="Optional"
                                  value={bi.remark || ''}
                                  onChange={e => updateBusinessItem(item.id, 'remark', e.target.value)}
                                  className="w-full min-w-[120px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex justify-end mt-5">
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={handleBusinessReview} disabled={!!actionLoading}
                className={`flex items-center gap-2 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg transition-all ${
                  businessAction === 'gm'
                    ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                    : 'bg-red-500 hover:bg-red-600 shadow-red-100'
                }`}>
                {actionLoading === 'business' ? <Loader2 size={16} className="animate-spin" /> : businessAction === 'gm' ? <CreditCard size={16} /> : <XCircle size={16} />}
                {businessAction === 'gm' ? 'Forward to GM' : 'Return to Branch'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── GM Review Action Panel ── */}
        {can.gmReview(role, status) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
            <h2 className="text-lg font-black text-slate-800 mb-2 flex items-center gap-2">
              <Briefcase size={20} className="text-orange-500" /> GM Review
            </h2>
            <p className="text-sm text-slate-500 mb-6">Review the business-assessed incidental and decide the next step.</p>

            {/* Action selector */}
            <div className="flex flex-wrap gap-3 mb-5">
              {[
                { value: 'accounts', label: 'Send to Accounts', color: 'purple' },
                { value: 'business', label: 'Return to Business', color: 'blue' },
                { value: 'branch', label: 'Return to Branch', color: 'red' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setGmAction(opt.value); setGmRemark(''); }}
                  className={`px-5 py-2.5 rounded-xl font-bold text-sm border transition-all ${
                    gmAction === opt.value
                      ? opt.value === 'accounts'
                        ? 'bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-100'
                        : opt.value === 'business'
                        ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100'
                        : 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-100'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Remark field */}
            <div className="mb-5">
              <label className="text-sm font-bold text-slate-700 mb-2 block">
                Remark
                {(gmAction === 'business' || gmAction === 'branch') && (
                  <span className="text-red-500 ml-1">*</span>
                )}
                {gmAction === 'accounts' && (
                  <span className="text-slate-400 text-xs font-normal ml-2">(optional)</span>
                )}
              </label>
              <textarea
                rows={3}
                value={gmRemark}
                onChange={e => setGmRemark(e.target.value)}
                placeholder={
                  gmAction === 'accounts'
                    ? 'Optional remark for accounts...'
                    : gmAction === 'business'
                    ? 'Explain what needs to be revised by business...'
                    : 'Explain what corrections are needed at branch...'
                }
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-orange-400 outline-none transition-all bg-slate-50 resize-none"
              />
            </div>

            <div className="flex justify-end">
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={handleGmReview} disabled={!!actionLoading}
                className={`flex items-center gap-2 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg transition-all ${
                  gmAction === 'accounts'
                    ? 'bg-purple-600 hover:bg-purple-700 shadow-purple-100'
                    : gmAction === 'business'
                    ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
                    : 'bg-red-500 hover:bg-red-600 shadow-red-100'
                }`}>
                {actionLoading === 'gm' ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />}
                {gmAction === 'accounts' ? 'Forward to Accounts' : gmAction === 'business' ? 'Return to Business' : 'Return to Branch'}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* ── Accounts Review Action Panel ── */}
        {can.accountsReview(role, status) && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white rounded-3xl shadow-sm border border-slate-200 p-6 md:p-8 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
                <CreditCard size={20} className="text-purple-600" /> Accounts Review
              </h2>
              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setShowAccountsPanel(!showAccountsPanel)}
                className="text-sm font-bold text-purple-600 hover:bg-purple-50 px-4 py-2 rounded-xl transition-all">
                {showAccountsPanel ? 'Collapse' : 'Fill UTR Details'}
              </motion.button>
            </div>

            <AnimatePresence>
              {showAccountsPanel && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                  <p className="text-xs text-slate-500 mb-4">UTR No. is required for every item. This is the final step before the record is marked as processed.</p>
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="w-full text-left border-collapse min-w-[700px]">
                      <thead>
                        <tr className="bg-slate-50">
                          {['Particular', 'Paid Amount', 'UTR No. *', 'Remark'].map(h => (
                            <th key={h} className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item) => {
                          const ai = accountsItems.find(a => a.id === item.id) || {};
                          return (
                            <tr key={item.id} className="hover:bg-slate-50/30">
                              <td className="px-4 py-3 text-sm font-bold text-slate-800">{item.particular?.name}</td>
                              <td className="px-4 py-3 text-sm font-bold text-emerald-700">{fmtCurrency(item.incidental_amount_paid)}</td>
                              <td className="px-4 py-3">
                                <input type="text" placeholder="UTR Number"
                                  value={ai.utr_no || ''}
                                  onChange={e => updateAccountsItem(item.id, 'utr_no', e.target.value)}
                                  className="w-full min-w-[160px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-white font-mono" />
                              </td>
                              <td className="px-4 py-3">
                                <input type="text" placeholder="Optional"
                                  value={ai.remark || ''}
                                  onChange={e => updateAccountsItem(item.id, 'remark', e.target.value)}
                                  className="w-full min-w-[120px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-white" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end mt-4">
                    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      onClick={handleAccountsReview} disabled={!!actionLoading}
                      className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg shadow-purple-100 transition-all">
                      {actionLoading === 'accounts' ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                      Submit & Process
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* ── Return Modal ── */}
        <AnimatePresence>
          {showReturnModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowReturnModal(false)}>
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                    <XCircle size={22} className="text-red-500" /> Return to Branch Worker
                  </h3>
                  <button onClick={() => setShowReturnModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-xl transition"><X size={18} /></button>
                </div>
                <div className="p-6">
                  <label className="text-sm font-bold text-slate-700 mb-2 block">Reason for returning <span className="text-red-500">*</span></label>
                  <textarea rows="4" value={returnRemark} onChange={e => setReturnRemark(e.target.value)}
                    placeholder="Explain why this record is being returned..."
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 outline-none transition-all bg-slate-50 resize-none" />
                </div>
                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                  <button onClick={() => setShowReturnModal(false)}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-200 transition-all">Cancel</button>
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                    onClick={handleReturn} disabled={actionLoading === 'return'}
                    className="flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg transition-all">
                    {actionLoading === 'return' ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                    Return
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}
